import { createContext, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiPlus, mdiTransitConnectionVariant } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { EmptyState } from '../ui/empty-state'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import { AddRelationDialog } from './AddRelationDialog'
import { useAssetEquipment } from '../../hooks/useAssetEquipment'
import { useCan } from '../../contexts/PermissionsContext'
import type { components } from '../../api/schema'

type AssetRelation = components['schemas']['AssetRelation']
type AssetRelationRef = components['schemas']['AssetRelationRef']
type AssetRelationType = components['schemas']['AssetRelationType']
type PossibleRelationTypeItem = components['schemas']['PossibleRelationTypeItem']

/** The one relation type this section is about: TGBT → lot, sub-meter → lot,
 *  hot-water tank → lot. */
export const SERVES_RELATION = 'serves_distribution'

const RELATIONS_PATH = '/api/v1/organizations/{orgCode}/asset-relations'

/** Every `serves_distribution` edge whose source sits under the asset, one request. */
export function useSubtreeServes(org: string, assetUid: string | undefined) {
    const query = $api.useQuery(
        'get',
        RELATIONS_PATH,
        { params: { path: { orgCode: org }, query: { source_ancestor_uid: assetUid ?? '', relation_type: SERVES_RELATION, limit: 500 } } },
        { enabled: !!org && !!assetUid },
    )
    // The org-wide relations list answers `items` (not the usual `data`).
    const rows = useMemo(() => ((query.data as { items?: AssetRelation[] } | undefined)?.items ?? []), [query.data])
    /** source uid → the assets it serves. */
    const bySource = useMemo(() => {
        const out = new Map<string, AssetRelationRef[]>()
        for (const r of rows) {
            if (!r.source?.uid || !r.target) continue
            const list = out.get(r.source.uid) ?? []
            list.push(r.target)
            out.set(r.source.uid, list)
        }
        return out
    }, [rows])
    return { ...query, rows, bySource }
}

/**
 * What each equipment serves, read by the equipment rows (« Dessert : … »)
 * without drilling a prop through the trade accordion. Provided by the
 * Équipements tab; empty elsewhere.
 */
export const ServesContext = createContext<Map<string, AssetRelationRef[]>>(new Map())

/** The « Dessert : Lot 12, Lot 14 » line under an equipment name. */
export function ServesLine({ org, equipmentUid }: { org: string; equipmentUid: string }) {
    const { t } = useTranslation('assets')
    const served = useContext(ServesContext).get(equipmentUid)
    if (!served || served.length === 0) return null
    return (
        <div className="mt-0.5 truncate text-[11px] text-muted-foreground" data-testid={`equipment-serves-${equipmentUid}`}>
            {t('networks.serves')}{' '}
            {served.slice(0, 4).map((target, i) => (
                <span key={target.uid}>
                    {i > 0 && ', '}
                    <Link to="/$org/assets/$assetUid" params={{ org, assetUid: target.uid }} className="hover:underline">
                        {target.name || target.uid}
                    </Link>
                </span>
            ))}
            {served.length > 4 && ` +${served.length - 4}`}
        </div>
    )
}

/**
 * « Réseaux et dessertes » (spec 2026-10-25-24): every serving link under the
 * asset, grouped by the equipment that serves, at the bottom of the Équipements
 * tab. It replaces the generic « Actifs liés » block there, which mixed these
 * networks with easements and management links.
 */
export function NetworksSection({ org, assetUid }: { org: string; assetUid: string }) {
    const { t } = useTranslation('assets')
    const canWrite = useCan('assets.write')
    const { rows, bySource, isLoading } = useSubtreeServes(org, assetUid)
    const [addOpen, setAddOpen] = useState(false)

    const groups = useMemo(() => {
        const sources = new Map<string, AssetRelationRef>()
        for (const r of rows) if (r.source?.uid) sources.set(r.source.uid, r.source)
        return [...sources.values()]
            .map((source) => ({ source, targets: bySource.get(source.uid) ?? [] }))
            .sort((a, b) => (a.source.name ?? '').localeCompare(b.source.name ?? ''))
    }, [rows, bySource])

    return (
        <section className="space-y-4 rounded-lg border border-border bg-card p-4" data-testid="networks-section">
            <header className="flex items-center gap-2">
                <AppIcon tone="muted" path={mdiTransitConnectionVariant} size="md" />
                <h2 className="text-base font-medium text-foreground">{t('networks.title')}</h2>
                {rows.length > 0 && <span className="text-xs text-muted-foreground">({rows.length})</span>}
                {canWrite && (
                    <div className="ml-auto">
                        <Button type="button" variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="networks-add-btn" aria-label={t('networks.add')} title={t('networks.add')}>
                            <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{t('networks.add')}</span>
                        </Button>
                    </div>
                )}
            </header>

            {!isLoading && groups.length === 0 && (
                <EmptyState variant="inline" icon={mdiTransitConnectionVariant} title={t('networks.empty')} data-testid="networks-empty" />
            )}

            {groups.length > 0 && (
                <ul className="divide-y divide-border/60">
                    {groups.map(({ source, targets }) => (
                        <li key={source.uid} className="flex flex-col gap-1 py-2 sm:flex-row sm:items-baseline sm:gap-4" data-testid={`networks-row-${source.uid}`}>
                            <Link to="/$org/assets/$assetUid" params={{ org, assetUid: source.uid }} className="w-64 shrink-0 truncate text-sm font-medium hover:underline">
                                {source.name || source.uid}
                            </Link>
                            <div className="flex flex-wrap gap-1.5 text-xs">
                                <span className="text-muted-foreground">{t('networks.serves')}</span>
                                {targets.map((target) => (
                                    <Link
                                        key={target.uid}
                                        to="/$org/assets/$assetUid"
                                        params={{ org, assetUid: target.uid }}
                                        className="rounded bg-muted px-1.5 py-0.5 hover:bg-muted/70"
                                    >
                                        {target.name || target.uid}
                                    </Link>
                                ))}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {addOpen && <AddServesFlow org={org} assetUid={assetUid} onClose={() => setAddOpen(false)} />}
        </section>
    )
}

/**
 * Pick the serving equipment, then the served assets: the relation dialog is
 * anchored on its source, and the section sits on the building, so the source
 * is chosen first among the equipment under it.
 */
function AddServesFlow({ org, assetUid, onClose }: { org: string; assetUid: string; onClose: () => void }) {
    const { t } = useTranslation('assets')
    const { equipment, isLoading } = useAssetEquipment(org, assetUid)
    const [sourceUid, setSourceUid] = useState('')
    const source = equipment.find((e) => e.uid === sourceUid)

    const { data: catalogData } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/asset-relation-types', { params: { path: { orgCode: org } } })
    const catalog = useMemo(() => {
        const items = (catalogData as { data?: AssetRelationType[] } | undefined)?.data ?? []
        return Object.fromEntries(items.map((rt) => [rt.code, rt]))
    }, [catalogData])
    const { data: possibleData } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}/possible-relation-types',
        { params: { path: { orgCode: org, assetTypeUid: source?.asset_type_uid ?? '' } } },
        { enabled: !!source?.asset_type_uid },
    )
    const possibleTypes = useMemo<PossibleRelationTypeItem[]>(
        () => ((possibleData as { data?: PossibleRelationTypeItem[] } | undefined)?.data ?? []).filter((p) => p.code === SERVES_RELATION),
        [possibleData],
    )
    const sorted = useMemo(() => [...equipment].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')), [equipment])

    if (source && possibleTypes.length > 0) {
        return (
            <AddRelationDialog
                org={org}
                assetUid={source.uid}
                open
                onOpenChange={(o) => { if (!o) onClose() }}
                possibleTypes={possibleTypes}
                catalog={catalog}
                title={t('networks.add')}
            />
        )
    }

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
            <DialogContent className="sm:max-w-md" data-testid="networks-source-dialog">
                <DialogHeader>
                    <DialogTitle>{t('networks.add')}</DialogTitle>
                    <DialogDescription>{t('networks.pick_source')}</DialogDescription>
                </DialogHeader>
                <Select value={sourceUid} onValueChange={setSourceUid} disabled={isLoading}>
                    <SelectTrigger data-testid="networks-source-select">
                        <SelectValue placeholder={t('networks.source_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                        {sorted.map((e) => (
                            <SelectItem key={e.uid} value={e.uid} data-testid={`networks-source-option-${e.uid}`}>
                                {e.name || e.uid}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {source && possibleData && possibleTypes.length === 0 && (
                    <p className="text-xs text-muted-foreground" data-testid="networks-source-cannot-serve">{t('networks.cannot_serve')}</p>
                )}
            </DialogContent>
        </Dialog>
    )
}
